// ============================================
// Trading Interface - Order Book & Trade Panel (International)
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaChevronDown, FaGift, FaTh, FaEllipsisV,
  FaHome, FaChartBar, FaEye, FaFileAlt, FaInfoCircle, FaMinus, FaPlus
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import socketService from '../services/socket';
import './International.css';

const International = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [selectedPair, setSelectedPair] = useState('AAPL/USD');
  const [tradeType, setTradeType] = useState('buy'); // 'buy' or 'sell'
  const [orderType, setOrderType] = useState('limit');
  const [price, setPrice] = useState('189.45');
  const [amount, setAmount] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(189.45);
  const [priceChange, setPriceChange] = useState(1.25);
  const [loading, setLoading] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [priceAnimation, setPriceAnimation] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);
  const [showOrderTypeTooltip, setShowOrderTypeTooltip] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const priceRef = useRef(189.45);

  // Order book data state - Limited to 4 orders each
  const [sellOrders, setSellOrders] = useState([
    { price: 189.50, amount: 125.30 },
    { price: 189.48, amount: 98.75 },
    { price: 189.46, amount: 156.20 },
    { price: 189.45, amount: 87.40 },
  ]);

  const [buyOrders, setBuyOrders] = useState([
    { price: 189.45, amount: 142.60 },
    { price: 189.43, amount: 203.15 },
    { price: 189.41, amount: 178.90 },
    { price: 189.40, amount: 95.25 },
  ]);

  // Fetch user balance and refresh periodically
  useEffect(() => {
    if (user) {
      setAvailableBalance(user.balance || 0);
    }
    
    // Refresh balance every 10 seconds
    const balanceInterval = setInterval(async () => {
      if (refreshUser) {
        await refreshUser();
      }
    }, 10000);
    
    return () => clearInterval(balanceInterval);
  }, [user, refreshUser]);

  // Fetch price from Binance API (real-time for crypto pairs)
  const fetchPriceFromBinance = useCallback(async () => {
    try {
      // Check if selected pair contains crypto that can be fetched from Binance
      const pairParts = selectedPair.split('/');
      const baseSymbol = pairParts[0];
      
      // Map of symbols that can be fetched from Binance
      const binancePairs = {
        'BTC': 'BTCUSDT',
        'ETH': 'ETHUSDT',
        'BNB': 'BNBUSDT',
        'XRP': 'XRPUSDT',
        'ADA': 'ADAUSDT',
        'SOL': 'SOLUSDT',
        'DOGE': 'DOGEUSDT',
        'TRX': 'TRXUSDT',
        'USDT': 'BUSDUSDT'
      };
      
      const binanceSymbol = binancePairs[baseSymbol];
      if (binanceSymbol && (pairParts[1] === 'USDT' || pairParts[1] === 'USD')) {
        const response = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${binanceSymbol}`);
        if (response.ok) {
          const data = await response.json();
          const newPrice = parseFloat(data.lastPrice);
          const previousPrice = parseFloat(data.prevClosePrice);
          const changePercent = previousPrice > 0 
            ? ((newPrice - previousPrice) / previousPrice) * 100 
            : 0;
          
          const oldPrice = priceRef.current;
          if (Math.abs(newPrice - oldPrice) > 0.001) {
            setPriceAnimation(newPrice > oldPrice ? 'up' : 'down');
            setTimeout(() => setPriceAnimation(null), 500);
            setCurrentPrice(newPrice);
            setPriceChange(changePercent);
            priceRef.current = newPrice;
            
            // Update price input if it matches current price
            if (orderType === 'market' || Math.abs(parseFloat(price) - oldPrice) < 0.01) {
              setPrice(newPrice.toFixed(2));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching price from Binance:', error);
    }
  }, [selectedPair, orderType, price]);

  // Real-time price updates via WebSocket and Binance API
  useEffect(() => {
    const socket = socketService.connect();
    
    // Fetch initial price from Binance
    fetchPriceFromBinance();
    
    // Update price every 2 seconds from Binance API
    const priceInterval = setInterval(() => {
      fetchPriceFromBinance();
    }, 2000);
    
    const handlePriceUpdate = (updatedAssets) => {
      // Find selected pair or use default
      const asset = updatedAssets.find(a => a.symbol === selectedPair.split('/')[0] || a.pair === selectedPair);
      if (asset) {
        const newPrice = parseFloat(asset.price) || currentPrice;
        const oldPrice = priceRef.current;
        
        // Animate price change
        if (newPrice !== oldPrice) {
          setPriceAnimation(newPrice > oldPrice ? 'up' : 'down');
          setTimeout(() => setPriceAnimation(null), 500);
          
          setCurrentPrice(newPrice);
          setPriceChange(asset.priceChange || priceChange);
          priceRef.current = newPrice;
          
          // Update price input if it matches current price
          if (Math.abs(parseFloat(price) - oldPrice) < 0.01) {
            setPrice(newPrice.toFixed(2));
          }
        }
        
        // Update order book with real-time data
        if (asset.orderBook) {
          if (asset.orderBook.sell && asset.orderBook.sell.length > 0) {
            const newSellOrders = asset.orderBook.sell.slice(0, 4);
            setSellOrders(prev => {
              const changed = JSON.stringify(prev) !== JSON.stringify(newSellOrders);
              return changed ? newSellOrders : prev;
            });
          }
          if (asset.orderBook.buy && asset.orderBook.buy.length > 0) {
            const newBuyOrders = asset.orderBook.buy.slice(0, 4);
            setBuyOrders(prev => {
              const changed = JSON.stringify(prev) !== JSON.stringify(newBuyOrders);
              return changed ? newBuyOrders : prev;
            });
          }
        }
      }
    };
    
    socketService.onPriceUpdate(handlePriceUpdate);

    return () => {
      clearInterval(priceInterval);
      socketService.off('price_update', handlePriceUpdate);
      socketService.disconnect();
    };
  }, [currentPrice, price, priceChange, selectedPair, fetchPriceFromBinance]);

  // Auto-update price when orderType changes to market
  useEffect(() => {
    if (orderType === 'market') {
      setPrice(currentPrice.toFixed(2));
    }
  }, [orderType, currentPrice]);

  // Update price when clicking on order book
  const handleOrderBookClick = (orderPrice, orderType) => {
    setPrice(orderPrice.toFixed(2));
    if (orderType === 'sell') {
      setTradeType('sell');
    } else {
      setTradeType('buy');
    }
  };

  const handlePriceAdjust = (direction) => {
    const current = parseFloat(price) || 0;
    const step = 0.01;
    if (direction === 'up') {
      setPrice((current + step).toFixed(2));
    } else {
      setPrice(Math.max(0, current - step).toFixed(2));
    }
  };

  const handleAmountAdjust = (direction) => {
    const current = parseFloat(amount) || 0;
    const step = 1;
    if (direction === 'up') {
      setAmount((current + step).toFixed(2));
    } else {
      setAmount(Math.max(0, current - step).toFixed(2));
    }
  };

  const handlePercentageChange = (percent) => {
    setPercentage(percent);
    if (percent > 0 && availableBalance > 0) {
      const calculatedAmount = (availableBalance * (percent / 100)) / parseFloat(price);
      setAmount(calculatedAmount.toFixed(2));
    } else {
      setAmount('');
    }
  };

  const total = parseFloat(price) * parseFloat(amount) || 0;

  const handleTrade = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('กรุณากรอกจำนวนที่ต้องการเทรด');
      return;
    }

    if (orderType === 'limit' && (!price || parseFloat(price) <= 0)) {
      toast.error('กรุณากรอกราคาที่ต้องการเทรด');
      return;
    }

    if (tradeType === 'buy' && total > availableBalance) {
      toast.error('ยอดเงินคงเหลือไม่เพียงพอ');
      return;
    }

    // Show confirmation dialog
    setConfirmDialog({
      type: tradeType,
      pair: selectedPair,
      orderType: orderType,
      price: orderType === 'limit' ? parseFloat(price) : currentPrice,
      amount: parseFloat(amount),
      total: total,
    });
  };

  const confirmTrade = async () => {
    if (!confirmDialog) return;

    setLoading(true);
    try {
      // TODO: Implement actual trade API call
      toast.success(`${confirmDialog.type === 'buy' ? 'ซื้อ' : 'ขาย'} ${confirmDialog.pair} สำเร็จ`);
      
      // Add to open orders
      setOpenOrders(prev => [...prev, {
        id: Date.now(),
        pair: confirmDialog.pair,
        type: confirmDialog.type,
        orderType: confirmDialog.orderType,
        price: confirmDialog.price,
        amount: confirmDialog.amount,
        total: confirmDialog.total,
        status: 'pending',
        createdAt: new Date(),
      }]);
      
      // Reset form
      setAmount('');
      setPercentage(0);
      setConfirmDialog(null);
      
      // Refresh user balance
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการเทรด');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="foreign-currency-page">
      {/* Top Header */}
      <div className="fc-header">
        <div className="fc-header-left">
          <div className="fc-pair-selector">
            <span className="fc-pair-name">{selectedPair}</span>
            <FaChevronDown className="fc-dropdown-icon" />
          </div>
          <div className={`fc-price-change ${priceChange >= 0 ? 'positive' : 'negative'}`}>
            {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
          </div>
        </div>
        <div className="fc-header-right">
          <button className="fc-header-icon">
            <FaGift />
          </button>
          <button className="fc-header-icon">
            <FaTh />
          </button>
          <button className="fc-header-icon">
            <FaEllipsisV />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="fc-main-content">
        {/* Left: Order Book */}
        <div className="fc-orderbook">
          <div className="fc-orderbook-header">
            <span>ราคา (USD)</span>
            <span>จำนวน</span>
          </div>

          {/* Sell Orders - Top */}
          <div className="fc-sell-orders">
            {sellOrders.map((order, index) => (
              <div 
                key={index} 
                className="fc-order-row sell"
                onClick={() => handleOrderBookClick(order.price, 'sell')}
              >
                <span className="fc-order-price">{order.price.toFixed(2)}</span>
                <div className="fc-order-amount-container">
                  <div 
                    className="fc-order-bar sell-bar"
                    style={{ width: `${(order.amount / 203.15) * 100}%` }}
                  ></div>
                  <span className="fc-order-amount">{order.amount.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Current Price - Center */}
          <motion.div 
            className="fc-current-price"
            animate={priceAnimation ? {
              scale: [1, 1.05, 1],
              backgroundColor: priceAnimation === 'up' 
                ? ['#fafafa', '#e8f5e9', '#fafafa']
                : ['#fafafa', '#ffebee', '#fafafa']
            } : {}}
            transition={{ duration: 0.5 }}
          >
            <div className={`fc-price-large ${priceAnimation ? `price-${priceAnimation}` : ''}`}>
              {currentPrice.toFixed(2)}
            </div>
            <div className="fc-price-thb">≈ ${currentPrice.toFixed(2)}</div>
            <div className={`fc-price-change-small ${priceChange >= 0 ? 'positive' : 'negative'}`}>
              {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
            </div>
          </motion.div>

          {/* Buy Orders - Bottom */}
          <div className="fc-buy-orders">
            {buyOrders.map((order, index) => (
              <div 
                key={index} 
                className="fc-order-row buy"
                onClick={() => handleOrderBookClick(order.price, 'buy')}
              >
                <span className="fc-order-price">{order.price.toFixed(2)}</span>
                <div className="fc-order-amount-container">
                  <div 
                    className="fc-order-bar buy-bar"
                    style={{ width: `${(order.amount / 203.15) * 100}%` }}
                  ></div>
                  <span className="fc-order-amount">{order.amount.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Controls */}
          <div className="fc-orderbook-controls">
            <input type="text" className="fc-control-input" defaultValue="0.01" />
            <button className="fc-control-icon">
              <FaTh />
            </button>
          </div>
        </div>

        {/* Right: Trade Panel */}
        <div className="fc-trade-panel">
          <form onSubmit={handleTrade}>
            {/* Trade Type Tabs */}
            <div className="fc-trade-tabs">
              <button
                type="button"
                className={`fc-tab ${tradeType === 'buy' ? 'active buy' : ''}`}
                onClick={() => setTradeType('buy')}
              >
                ซื้อ
              </button>
              <button
                type="button"
                className={`fc-tab ${tradeType === 'sell' ? 'active sell' : ''}`}
                onClick={() => setTradeType('sell')}
              >
                ขาย
              </button>
            </div>

          {/* Order Type */}
          <div 
            className="fc-order-type"
            onMouseEnter={() => setShowOrderTypeTooltip(true)}
            onMouseLeave={() => setShowOrderTypeTooltip(false)}
          >
            <FaInfoCircle className="fc-info-icon" />
            <select 
              className="fc-order-type-select"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            >
              <option value="limit">ลิมิต</option>
              <option value="market">ตลาด</option>
            </select>
            <FaChevronDown className="fc-select-arrow" />
            {showOrderTypeTooltip && (
              <div className="fc-tooltip">
                <div className="fc-tooltip-content">
                  <strong>ลิมิต:</strong> กำหนดราคาที่ต้องการซื้อ/ขาย<br/>
                  <strong>ตลาด:</strong> ซื้อ/ขายทันทีตามราคาปัจจุบัน
                </div>
              </div>
            )}
          </div>

          {/* Price Input */}
          {orderType === 'limit' && (
            <div className="fc-input-group">
              <label className="fc-input-label">ราคา (USD)</label>
              <div className="fc-input-with-buttons">
                <button 
                  type="button"
                  className="fc-input-btn"
                  onClick={() => handlePriceAdjust('down')}
                >
                  <FaMinus />
                </button>
                <input
                  type="number"
                  className="fc-input"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  step="0.01"
                  required={orderType === 'limit'}
                />
                <button 
                  type="button"
                  className="fc-input-btn"
                  onClick={() => handlePriceAdjust('up')}
                >
                  <FaPlus />
                </button>
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div className="fc-input-group">
            <label className="fc-input-label">จำนวน</label>
            <div className="fc-input-with-buttons">
              <button 
                type="button"
                className="fc-input-btn"
                onClick={() => handleAmountAdjust('down')}
              >
                <FaMinus />
              </button>
              <input
                type="number"
                className="fc-input"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (e.target.value && availableBalance > 0) {
                    const percent = ((parseFloat(e.target.value) * parseFloat(price)) / availableBalance) * 100;
                    setPercentage(Math.min(100, Math.max(0, percent)));
                  } else {
                    setPercentage(0);
                  }
                }}
                placeholder="0.00"
                step="0.01"
                required
              />
              <button 
                type="button"
                className="fc-input-btn"
                onClick={() => handleAmountAdjust('up')}
              >
                <FaPlus />
              </button>
            </div>
          </div>

          {/* Percentage Slider */}
          <div className="fc-percentage-slider">
            <div className="fc-slider-track">
              {[0, 25, 50, 75, 100].map((val) => (
                <div
                  key={val}
                  className={`fc-slider-marker ${percentage >= val ? 'active' : ''}`}
                  onClick={() => handlePercentageChange(val)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handlePercentageChange(val);
                    }
                  }}
                ></div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="fc-total-group">
            <label className="fc-input-label">ทั้งหมด (USD)</label>
            <div className="fc-total-value">{total.toFixed(2)}</div>
          </div>

          {/* Available Balance */}
          <div className="fc-balance-group">
            <label className="fc-input-label">คงเหลือ</label>
            <div className="fc-balance-value">
              {availableBalance.toFixed(2)} {tradeType === 'buy' ? 'USD' : 'USD'} 
              <span 
                style={{ color: '#667eea', cursor: 'pointer', marginLeft: '4px' }}
                onClick={() => navigate('/deposit')}
                role="button"
                tabIndex={0}
              >
                +
              </span>
            </div>
          </div>

          {/* Action Button */}
          <button 
            type="submit"
            className={`fc-action-btn ${tradeType === 'buy' ? 'buy-btn' : 'sell-btn'}`}
            disabled={loading}
          >
            {loading ? 'กำลังดำเนินการ...' : `${tradeType === 'buy' ? 'ซื้อ' : 'ขาย'} ${selectedPair.split('/')[0]}`}
          </button>
          </form>
        </div>
      </div>

      {/* Bottom: Open Orders */}
      <div className="fc-open-orders">
        <div className="fc-open-orders-header">
          <span className="fc-open-orders-title">
            รายการที่เปิดไว้ ({openOrders.length})
          </span>
          <button 
            className="fc-view-icon"
            onClick={() => navigate('/history')}
            title="ดูประวัติการเทรด"
          >
            <FaEye />
          </button>
        </div>
        {openOrders.length > 0 && (
          <div className="fc-open-orders-list">
            {openOrders.slice(0, 3).map((order) => (
              <div key={order.id} className="fc-open-order-item">
                <span className={`fc-order-type-badge ${order.type}`}>
                  {order.type === 'buy' ? 'ซื้อ' : 'ขาย'}
                </span>
                <span className="fc-order-pair">{order.pair}</span>
                <span className="fc-order-price-small">{order.price.toFixed(2)}</span>
                <span className="fc-order-amount-small">{order.amount}</span>
              </div>
            ))}
            {openOrders.length > 3 && (
              <div className="fc-open-order-more">
                +{openOrders.length - 3} รายการ
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {confirmDialog && (
          <motion.div
            className="fc-confirm-dialog-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setConfirmDialog(null)}
          >
            <motion.div
              className="fc-confirm-dialog"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="fc-confirm-header">
                <h3>ยืนยันการเทรด</h3>
                <button 
                  className="fc-confirm-close"
                  onClick={() => setConfirmDialog(null)}
                >
                  ×
                </button>
              </div>
              <div className="fc-confirm-content">
                <div className="fc-confirm-row">
                  <span>ประเภท:</span>
                  <span className={`fc-confirm-value ${confirmDialog.type}`}>
                    {confirmDialog.type === 'buy' ? 'ซื้อ' : 'ขาย'}
                  </span>
                </div>
                <div className="fc-confirm-row">
                  <span>คู่เทรด:</span>
                  <span className="fc-confirm-value">{confirmDialog.pair}</span>
                </div>
                <div className="fc-confirm-row">
                  <span>ประเภทคำสั่ง:</span>
                  <span className="fc-confirm-value">
                    {confirmDialog.orderType === 'limit' ? 'ลิมิต' : 'ตลาด'}
                  </span>
                </div>
                <div className="fc-confirm-row">
                  <span>ราคา:</span>
                  <span className="fc-confirm-value">{confirmDialog.price.toFixed(2)} USD</span>
                </div>
                <div className="fc-confirm-row">
                  <span>จำนวน:</span>
                  <span className="fc-confirm-value">{confirmDialog.amount}</span>
                </div>
                <div className="fc-confirm-row fc-confirm-total">
                  <span>ทั้งหมด:</span>
                  <span className="fc-confirm-value">{confirmDialog.total.toFixed(2)} USD</span>
                </div>
              </div>
              <div className="fc-confirm-actions">
                <button
                  className="fc-confirm-btn cancel"
                  onClick={() => setConfirmDialog(null)}
                  disabled={loading}
                >
                  ยกเลิก
                </button>
                <button
                  className={`fc-confirm-btn confirm ${confirmDialog.type}`}
                  onClick={confirmTrade}
                  disabled={loading}
                >
                  {loading ? 'กำลังดำเนินการ...' : 'ยืนยัน'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default International;
